import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Mail, Lock, Eye, EyeOff, Loader2 } from "lucide-react";
import { loginStaff } from "@/Calls/callStaffAuth";
import { useAuth } from "@/hooks/useAuth";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";

interface Props {
    title: string;
    subtitle?: string;
    backgroundImage: string;
}

export default function StaffLoginForm({ title, subtitle, backgroundImage }: Props) {
    const [email, setEmail] = useState("");
    const [password, setPassword] = useState("");
    const [showPassword, setShowPassword] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState<string | null>(null);

    const navigate = useNavigate();
    const { setUser } = useAuth();

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();
        setLoading(true);
        setError(null);

        try {
            const res = await loginStaff(email, password);
            if (res?.ok && res.staff) {
                setUser({
                    id: res.staff.staff_id,
                    role: res.staff.role.toUpperCase(),
                    dealerId: res.staff.dealer_id ? String(res.staff.dealer_id) : undefined,
                    page: res.redirect ?? "/service/dashboard",
                });
                navigate(res.redirect ?? "/service/dashboard", { replace: true });
            } else {
                setError(res?.message || "Login failed — please check your credentials.");
            }
        } catch (err) {
            if (err instanceof Error) setError(err.message);
            else setError("Failed to connect to server.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="relative flex min-h-screen w-full items-center justify-center overflow-hidden p-4">
            <div className="z-10 w-full max-w-6xl">
                <div className="bg-secondary/50 overflow-hidden rounded-[40px] shadow-2xl">
                    <div className="grid min-h-[700px] lg:grid-cols-2">
                        <div
                            className="relative m-4 rounded-3xl bg-cover bg-center p-12 text-white"
                            style={{ backgroundImage: `url('${backgroundImage}')` }}
                        />
                        <div className="flex flex-col justify-center p-12">
                            <div className="mx-auto w-full max-w-md">
                                <div className="mb-8 text-center">
                                    <h2 className="text-3xl font-light uppercase">{title}</h2>
                                    <p className="mt-2 text-sm text-stone-600">
                                        {subtitle ?? "Sign in with your portal credentials"}
                                    </p>
                                </div>

                                <form onSubmit={handleSubmit} className="space-y-6">
                                    <div>
                                        <label htmlFor="email" className="mb-2 block text-sm font-medium uppercase">
                                            Email
                                        </label>
                                        <div className="relative">
                                            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                            <Input
                                                id="email"
                                                type="email"
                                                value={email}
                                                onChange={(e) => setEmail(e.target.value)}
                                                required
                                                className="border-border bg-input block w-full rounded-lg border py-3 pr-3 pl-10 text-sm"
                                                placeholder="Enter your email address"
                                            />
                                        </div>
                                    </div>

                                    <div>
                                        <label htmlFor="password" className="mb-2 block text-sm font-medium uppercase">
                                            Password
                                        </label>
                                        <div className="relative">
                                            <Lock className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-gray-400" />
                                            <Input
                                                id="password"
                                                type={showPassword ? "text" : "password"}
                                                value={password}
                                                onChange={(e) => setPassword(e.target.value)}
                                                required
                                                className="border-border bg-input block w-full rounded-lg border py-3 pr-12 pl-10 text-sm"
                                                placeholder="Enter your password"
                                            />
                                            <Button
                                                type="button"
                                                variant="ghost"
                                                size="sm"
                                                className="absolute right-3 top-1/2 -translate-y-1/2 h-auto p-0"
                                                onClick={() => setShowPassword(!showPassword)}
                                            >
                                                {showPassword ? (
                                                    <EyeOff className="h-5 w-5 text-gray-400" />
                                                ) : (
                                                    <Eye className="h-5 w-5 text-gray-400" />
                                                )}
                                            </Button>
                                        </div>
                                    </div>

                                    {error && <p className="text-red-500 text-sm text-center">{error}</p>}

                                    <Button
                                        type="submit"
                                        className="relative flex w-full items-center justify-center rounded-lg bg-gradient-to-r from-indigo-500 to-violet-500 px-4 py-3 text-sm font-medium text-white transition-all duration-300 hover:opacity-90"
                                        disabled={loading}
                                    >
                                        {loading ? (
                                            <>
                                                <Loader2 className="h-5 w-5 animate-spin" />
                                                <span className="ml-2">Signing in...</span>
                                            </>
                                        ) : (
                                            "Sign in to Portal"
                                        )}
                                    </Button>
                                </form>
                            </div>
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
}
